import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Trash2, ShieldAlert, Ban, XCircle,
  Clock, Filter, RefreshCw, CheckCircle
} from 'lucide-react';
import { moderationAPI } from '../../services/api';

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`} />
);

const STATUS_FILTERS = [
  { value: '',             label: 'All' },
  { value: 'pending',      label: 'Pending' },
  { value: 'reviewed',     label: 'Reviewed' },
  { value: 'action_taken', label: 'Actioned' },
  { value: 'dismissed',    label: 'Dismissed' },
];

const CONTENT_TYPE_FILTERS = [
  { value: '',      label: 'All Types' },
  { value: 'post',  label: 'Posts' },
  { value: 'user',  label: 'Users' },
  { value: 'comment', label: 'Comments' },
];

export default function ReportedContentQueue() {
  const [reports, setReports]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [warning, setWarning]     = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter]     = useState('');
  const [page, setPage]           = useState(1);
  const [actioningId, setActioningId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { reportId, action }

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.content_type = typeFilter;

      const res = await moderationAPI.listReports(params);
      if (res.success) {
        setReports(res.data.reports || []);
        setWarning(res.data.warning || null);
      }
    } catch (err) {
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, page]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const runAction = async (reportId, actionFn) => {
    setActioningId(reportId);
    try {
      await actionFn(reportId);
      await fetchReports();
    } catch (err) {
      alert(`Action failed: ${err}`);
    } finally {
      setActioningId(null);
      setConfirmAction(null);
    }
  };

  const handleDismiss = (id) =>
    runAction(id, (rid) => moderationAPI.updateReport(rid, { status: 'dismissed' }));

  const handleDeleteContent = (id) =>
    runAction(id, (rid) => moderationAPI.deleteReportedContent(rid));

  const handleWarnUser = (id) =>
    runAction(id, (rid) => moderationAPI.warnUser(rid));

  const handleBanUser = (id) =>
    runAction(id, (rid) => moderationAPI.banUserFromReport(rid));

  const timeAgo = (ts) => {
    if (!ts) return 'Unknown';
    const m = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const statusBadge = (status) => {
    const cfg = {
      pending:      { cls: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
      reviewed:     { cls: 'bg-blue-100 text-blue-700',     label: 'Reviewed' },
      action_taken: { cls: 'bg-green-100 text-green-700',   label: 'Actioned' },
      dismissed:    { cls: 'bg-gray-100 text-gray-500',     label: 'Dismissed' },
    };
    const c = cfg[status] || cfg.pending;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.cls}`}>{c.label}</span>;
  };

  const contentTypeBadge = (type) => (
    <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full capitalize">{type}</span>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <ShieldAlert className="text-red-600" size={24} />
            Reported Content Queue
          </h1>
          <p className="text-sm text-gray-500">Review and action user reports</p>
        </div>
        <button onClick={fetchReports}
          className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm hover:border-red-400 transition">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>Backend note:</strong> {warning} — this usually means the <code>reports</code> table
            hasn't been created in the database yet. The UI is fully wired and will populate once the
            table exists and reports come in.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <strong>Error:</strong> {error}
          <button onClick={fetchReports} className="ml-3 underline">Retry</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500 font-medium">Status:</span>
          {STATUS_FILTERS.map(f => (
            <button key={f.value} onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                statusFilter === f.value ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">Type:</span>
          {CONTENT_TYPE_FILTERS.map(f => (
            <button key={f.value} onClick={() => { setTypeFilter(f.value); setPage(1); }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                typeFilter === f.value ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reports list */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        {loading ? (
          <div className="space-y-3">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-16">
            <CheckCircle size={40} className="text-green-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No reports match this filter</p>
            <p className="text-gray-300 text-sm mt-1">
              {statusFilter === 'pending' ? "You're all caught up." : 'Try a different filter.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <div key={report.id}
                className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-red-200 transition">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {statusBadge(report.status)}
                      {contentTypeBadge(report.content_type)}
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock size={11} /> {timeAgo(report.created_at)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      Reason: {report.reason}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Content ID: <span className="font-mono">{report.content_id}</span>
                      {report.reporter_id && (
                        <> · Reported by: <span className="font-mono">{report.reporter_id}</span></>
                      )}
                    </p>
                    {report.action_taken && (
                      <p className="text-xs text-green-600 mt-1">Action taken: {report.action_taken}</p>
                    )}
                  </div>

                  {/* Actions - only show for pending/reviewed reports */}
                  {(report.status === 'pending' || report.status === 'reviewed') && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        disabled={actioningId === report.id}
                        onClick={() => setConfirmAction({ reportId: report.id, action: 'delete' })}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition disabled:opacity-50">
                        <Trash2 size={12} /> Delete Content
                      </button>
                      <button
                        disabled={actioningId === report.id}
                        onClick={() => setConfirmAction({ reportId: report.id, action: 'warn' })}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-xs font-medium hover:bg-yellow-100 transition disabled:opacity-50">
                        <AlertTriangle size={12} /> Warn User
                      </button>
                      <button
                        disabled={actioningId === report.id}
                        onClick={() => setConfirmAction({ reportId: report.id, action: 'ban' })}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition disabled:opacity-50">
                        <Ban size={12} /> Ban User
                      </button>
                      <button
                        disabled={actioningId === report.id}
                        onClick={() => handleDismiss(report.id)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 text-gray-500 rounded-lg text-xs font-medium hover:bg-gray-100 transition disabled:opacity-50">
                        <XCircle size={12} /> Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm action modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Confirm Action</h3>
            <p className="text-sm text-gray-500 mb-5">
              {confirmAction.action === 'delete' && 'This will permanently delete the reported content. This cannot be undone.'}
              {confirmAction.action === 'warn' && 'This will send a warning notification to the user.'}
              {confirmAction.action === 'ban' && 'This will ban the user from the platform. This is a serious action.'}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition">
                Cancel
              </button>
              <button
                onClick={() => {
                  const { reportId, action } = confirmAction;
                  if (action === 'delete') handleDeleteContent(reportId);
                  if (action === 'warn') handleWarnUser(reportId);
                  if (action === 'ban') handleBanUser(reportId);
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}